import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  PrimaryButton,
  VideoPreview,
  DotLoader, 
  UploadVideoSkeleton
} from '@myplay/ui';
import { inject, observer } from 'mobx-react';
import InfiniteScroller from 'react-infinite-scroll-component';

import { UploadVideoModal, HeaderTiles, VideoPreviewActions, SceneLoaderWrapper } from '../../components';
import { capitalize } from '../../utils/stringManipulations';
import { GREEN_LIGHT, WHITE } from '../../styles/colors';
import {
  VIDEOS_BULK,
  MYPLAY_APP_ROUTE,
  AWAY,
  HOME,
  SHARE,
  DELETE,
  DOWNLOAD,
  ASSIGN_ASSISTANT,
  UPLOAD
} from '../../utils/constants';
import I18N from '../../I18N';
import SearchPanel from './SearchPanel';
import './Videos.scss';

const Videos = ({ VideosStore, TeamsStore, MetricsStore, ClubsStore }) => {
  const {
    videos,
    changeVideoTitle,
    updateGameScore,
    getClubVideos, 
    uploadPercent, 
    ongoingUploadTeamName, 
    ongoingUploadDate
  } = VideosStore;
  const { teams } = TeamsStore;
  const { currentClub } = ClubsStore;
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [filteredVideoTitle, setFilteredVideoTitle] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [ref, setRef] = useState(null);
  const [displayedModal, setDisplayedModal] = useState("");
  const [selectedVideoContainer, setSelectedVideoContainer] = useState({});
  const [currentTeamMembers, setCurrentTeamMembers] = useState(null);
  const [selectedRange, setRange] = useState(null);
  const [requestSkip, setRequestSkip] = useState(null);

  useEffect(() => {
    MetricsStore.setClubMetrics();
    setAllCategories();
  }, [teams.length]);

  const handleSearchChange = value => {
    setFilteredVideoTitle(value);
    setRequestSkip(0);
  }

  const handleTeamSelect = team => {
    setSelectedTeam(team);
    setRequestSkip(0);
  };

  const handleCategorySelect = category => {
    setSelectedCategory(category.name);
    setRequestSkip(0);
  };

  const setAllCategories = () => {
    const allCategories = [];
    teams.map(team => {
      const index = allCategories.findIndex(category => category.name === team.category.name);
      if (index === -1) {
        allCategories.push({ name: team.category.name });
      };
    });

    setCategories(allCategories);
  };

  const fetchVideos = async () => {
    setRequestSkip(skip => skip + 20);
    let filters = {};
    if (selectedTeam) {
      filters.teamId = selectedTeam._id;
    };

    if (selectedCategory) {
      filters.category = selectedCategory;
    };

    if (filteredVideoTitle) {
      filters.title = filteredVideoTitle;
    };

    if (selectedRange && selectedRange !== "Any time") {
      if (typeof selectedRange !== 'string') {
        filters.date = {
          startDate: new Date(selectedRange.from.format()),
          endTime: new Date(selectedRange.to.format())
        }
      } else {
        filters.date = {
          period: selectedRange === "Past week" ? "lastWeek" : "lastMonth"
        }
      }
    };

    await getClubVideos(currentClub.club._id, requestSkip, filters);
  }

  const handleVideoClick = videoId => {
    const route = `${MYPLAY_APP_ROUTE}/game/${videoId}`;
    window.open(route, '_blank');
  };

  const handleChangeGameTitle = (videoId, newTitle) => {
    changeVideoTitle(videoId, newTitle);
  };

  const handleUpdateAwayScore = (videoId, score) => {
    updateGameScore(videoId, score, AWAY);
  };

  const handleUpdateHomeScore = (videoId, score) => {
    updateGameScore(videoId, score, HOME);
  };

  const handleShare = videoContainer => {
    setSelectedVideoContainer(videoContainer);
    setDisplayedModal(SHARE);
  };

  const handleDelete = videoContainer => {
    setSelectedVideoContainer(videoContainer);
    setDisplayedModal(DELETE);
  };

  const handleDownload = videoContainer => {
    setSelectedVideoContainer(videoContainer);
    setDisplayedModal(DOWNLOAD);
  };

  const handleAssignAssistant = videoContainer => {
    setSelectedVideoContainer(videoContainer);
    const { members } = teams.filter(team => team._id === videoContainer.team._id)[0];
    setCurrentTeamMembers(members);
    setDisplayedModal(ASSIGN_ASSISTANT);
  };

  const closeModal = () => {
    setSelectedVideoContainer(null);
    setDisplayedModal("");
  };

  const handleRangePick = range => {
    setRange(range);
    setRequestSkip(0);
  };

  const handleClearFilters = async ()=> {
    try {
      await getClubVideos(currentClub.club._id);
      setRange(null);
      setSelectedTeam(null);
      setFilteredVideoTitle("");
      setSelectedCategory("");
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <SceneLoaderWrapper>
      <div className="videos" data-testid="videos">
        <div className="videos__header">
          <div className="videos__header__title-button-container">
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span className="videos__header__title-button-container__title">{I18N.t("VIDEOS").toUpperCase()}</span>
              <PrimaryButton
                background={GREEN_LIGHT}
                color={WHITE}
                className="videos__header__title-button-container__button"
                onClick={() => setDisplayedModal(UPLOAD)}
                data-testid="upload-video"
              >
                <span>{capitalize(I18N.t("UPLOAD_VIDEO"))}</span>
              </PrimaryButton>
            </div>
            <HeaderTiles />
          </div>
          <SearchPanel
            teams={teams}
            videos={videos || []}
            categories={categories}
            onRangePick={handleRangePick}
            onFilter={fetchVideos}
            onTeamSelect={handleTeamSelect}
            onCategorySelect={handleCategorySelect}
            onSearchChange={handleSearchChange}
            onClearFilters={handleClearFilters}
            selectedTeam={selectedTeam}
            selectedCategory={selectedCategory}
            filteredVideoTitle={filteredVideoTitle}
            selectedRange={selectedRange}
          />
        </div>
        <div className="videos__games-wrapper"
          ref={divElement => setRef(divElement)}
        >
          {ref &&
            <InfiniteScroller
              dataLength={videos ? videos.length : 0}
              next={fetchVideos}
              loader={<DotLoader />}
              scrollThreshold={0.95}
              height={ref.clientHeight}
              hasMore={videos ? videos.length > 10 : false}
            >
              <div className="videos__games-container">
                {uploadPercent &&
                  <div className="videos__games-container__video-preview-wrapper">
                    <UploadVideoSkeleton percent={uploadPercent} teamName={ongoingUploadTeamName} date={ongoingUploadDate}/>
                  </div>
                }
                {videos && videos.map((videoContainer, index) => {
                  const animationDelay = VIDEOS_BULK + 1 - videos.length + index;
                  return (
                    <div
                      key={`${videoContainer._id}${index}`}
                      className="videos__games-container__video-preview-wrapper"
                      style={{
                        animation: animationDelay <= 0 && "",
                        animationDelay: `${animationDelay * 0.2}s`
                      }}
                      data-testid="video-container"
                    >
                      <VideoPreview
                        videoContainer={videoContainer}
                        onDelete={handleDelete}
                        onDownload={handleDownload}
                        onClick={() => handleVideoClick(videoContainer._id)}
                        changeGameTitle={handleChangeGameTitle}
                        updateAwayScore={handleUpdateAwayScore}
                        updateHomeScore={handleUpdateHomeScore}
                        onShare={handleShare}
                        onAssignAssistant={handleAssignAssistant}
                      />
                    </div>
                  )
                }) 
              }
              {!videos && 
                <p className="videos__games-container__error">{capitalize(I18N.t("NO_RESULT_FOUND"))}</p>
              }
              </div>
            </InfiniteScroller>
          }
        </div>
        <VideoPreviewActions
          displayedModal={displayedModal}
          closeModal={closeModal}
          selectedVideoContainer={selectedVideoContainer}
          currentTeamMembers={currentTeamMembers}
        />
        <UploadVideoModal
          isVisible={displayedModal === UPLOAD}
          teams={teams}
          onClose={() => setDisplayedModal("")}
        />
      </div>
    </SceneLoaderWrapper>
  );
};

Videos.propTypes = {
  ClubsStore: PropTypes.object,
  MetricsStore: PropTypes.object,
  TeamsStore: PropTypes.object,
  VideosStore: PropTypes.object
};

Videos.defaultProps = {
  ClubsStore: {},
  MetricsStore: {},
  TeamsStore: {},
  VideosStore: {}
};

export default inject("VideosStore", "TeamsStore", "MetricsStore", "ClubsStore")(observer(Videos));